import { TextField, Theme } from '@mui/material';
import {
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';
import { FormProps } from '..';
import { createUseStyles } from 'react-jss';
import Icon, { IconType } from '../../icon';

interface Props extends FormProps {
  value: string;
  onChange?: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  onEnter?: (value: string) => void;
  type?: string;
  autoComplete?: string;
  rows?: number;
  size?: 'small' | 'medium';
  placeholder?: string;
  clearable?: boolean;
  startIcon?: IconType;
  readonly?: boolean;
}

const useStyles = createUseStyles<string, { isTextarea: boolean }>((theme: Theme) => ({
  textfield: ({ isTextarea }) => ({
    '& label.MuiFormLabel-root.Mui-focused': {
      color: theme.palette.common.black,
    },
    '& .MuiInputBase-root': {
      paddingRight: isTextarea ? 0 : 6,
      '&.Mui-focused': {
        '& fieldset': {
          borderColor: theme.palette.common.black,
        },
      },
      '&.Mui-readOnly fieldset': {
        borderColor: `${theme.palette.darkGray} !important`,
      },
      '&.MuiInputBase-adornedStart': {
        paddingLeft: 6,
      },
      '& input.MuiInputBase-inputAdornedEnd': {
        paddingRight: 12,
      },
      '& input.Mui-readOnly, textarea.Mui-readOnly': {
        color: `${theme.palette.darkGray} !important`,
      },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
    },
    ...(isTextarea && {
      '& textarea': {
        paddingRight: 14,
      },
    }),
  }),
  startAdornment: {
    marginTop: 4,
  },
  endAdornment: {
    marginRight: 6,
  },
  notchedOutline: {
    borderWidth: '1px !important',
  },
}));

const TextInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      label = '',
      onChange,
      onEnter,
      type = 'text',
      autoComplete = '',
      autoFocus = false,
      value,
      error = '',
      disabled = false,
      required = false,
      rows,
      size,
      placeholder,
      clearable = false,
      startIcon,
      readonly = false,
    },
    ref
  ) => {
    const isSmall = size === 'small';
    const isTextarea = type === 'textarea';
    const isPassword = type === 'password';
    const [showPassword, setShowPassword] = useState(false);
    const styles = useStyles({ isTextarea });

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (readonly) {
        return;
      }
      onChange?.(String(event.target.value), event);
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        onEnter?.(value);
      }
    };

    const TextareaProps = {
      multiline: false,
      minRows: 5,
      maxRows: 5,
    };

    if (isTextarea) {
      TextareaProps.multiline = true;
      if (rows) {
        TextareaProps.minRows = rows;
        TextareaProps.maxRows = rows;
      }
    }

    const handleClear = () => {
      if (readonly) {
        return;
      }
      onChange?.('');
    };

    let EndAdornmentEl: ReactNode = null;
    const showClearIcon = clearable && Boolean(value);
    const hasStartIcon = Boolean(startIcon);

    if (showClearIcon) {
      EndAdornmentEl = <Icon className={styles.endAdornment} name="close" onClick={handleClear} />;
    }

    if (isPassword) {
      EndAdornmentEl = (
        <Icon
          className={styles.endAdornment}
          name={showPassword ? 'visibility-off' : 'visibility'}
          onClick={() => setShowPassword((old) => !old)}
        />
      );
    }

    const compRef = useRef<HTMLInputElement>(null);

    // make sure autofocus actually works
    // https://stackoverflow.com/questions/60476155/is-it-safe-to-use-ref-current-as-useeffects-dependency-when-ref-points-to-a-dom
    useEffect(() => {
      if (!autoFocus) {
        return;
      }
      setTimeout(() => {
        (compRef || ref).current?.focus();
      });
    }, [compRef, ref, autoFocus]);

    return (
      <TextField
        id={name}
        name={name}
        label={label || undefined}
        variant="outlined"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={value || ''}
        margin={isSmall ? 'none' : 'normal'}
        fullWidth
        type={isPassword && showPassword ? 'text' : type}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        error={!!error}
        helperText={error}
        disabled={disabled}
        {...TextareaProps}
        className={styles.textfield}
        required={required}
        size={size}
        placeholder={placeholder}
        inputRef={ref || compRef}
        InputProps={{
          classes: {
            notchedOutline: styles.notchedOutline,
          },
          startAdornment: hasStartIcon ? (
            <Icon className={styles.startAdornment} name="search" />
          ) : null,
          endAdornment: EndAdornmentEl,
          readOnly: readonly,
        }}
      />
    );
  }
);

export default TextInput;
